import React, { useEffect, useRef, useState } from "react"
import { makeStyles } from "@mui/styles"
import ButtonFlawAction from "./ButtonFlawAction"
import TextField from "@mui/material/TextField"
import { COLORS } from "../../utils"
import SelectFlaw from "./SelectFlaw"
import SelectFlawOrigin from "./SelectFlawOrigin"
import { STOCK_TYPES, stockForType, isKnownStock } from "../../utils/stockTypes"
import clsx from "clsx"

const useStyles = makeStyles(() => ({
  withSelection: {
    fontSize: 30,
    fontWeight: "normal",
    lineHeight: "128.91%",
    marginTop: 57,
    "&.is-tablet": {
      marginTop: 52,
      fontSize: 37,
    },
    "& p": {
      margin: 0,
    },
    "& span": {
      fontWeight: "bold",
    },
  },
  reasons: {
    paddingTop: 28,
    "&.is-tablet": {
      paddingTop: 35,
    },
  },
  input: {
    fontWeight: "normal",
    fontSize: 16,
    color: COLORS.BLACK_CARD,
    lineHeight: "24px",
    width: 406,
    marginTop: 57,
    textAlign: "center",
  },
  error: {
    color: COLORS.RED,
    fontSize: 14,
    marginTop: 8,
    maxWidth: 406,
    textAlign: "center",
  },
  input1: {
    fontWeight: "normal",
    fontSize: 60,
    color: COLORS.BLACK_CARD,
    textAlign: "center",
    lineHeight: "70px",
    width: 126,
    marginTop: 12,
    padding: "0 12px",
  },
}))

const InputFlawNumberAndReason = (props) => {
  const {
    handleValidate,
    isTablet,
    setValue,
    lostActionInfos,
    flawProduct,
    productStock,
    hasTwoStocks = false,
    onOriginChange,
  } = props
  const classes = useStyles()
  const inputRef = useRef()

  const [flawStatus, setFlawStatus] = useState("")
  const [volume, setVolume] = useState("")
  const [origin, setOrigin] = useState(null)
  const [overflow, setOverflow] = useState(false)

  // A hub with a single fridge has no choice to offer, so the origin is settled from the start —
  // and its only stock is the click & collect one, which is what bounds the quantity there.
  const originChosen = !hasTwoStocks || origin !== null
  const boundingFridge = hasTwoStocks ? origin : STOCK_TYPES.CLICK_AND_COLLECT
  const availableStock = boundingFridge ? stockForType(productStock, boundingFridge) : undefined

  // One rule, one place: the quantity is brought back to the stock of whichever fridge bounds it.
  // Typing and picking a fridge both go through it, so the order of entry cannot matter.
  const capTo = (stock, asked) => {
    const tooMuch = isKnownStock(stock) && asked !== "" && parseInt(asked) > stock

    return { tooMuch, kept: tooMuch ? String(stock) : asked }
  }

  // Derived rather than kept in state: three handlers used to recompute it, and the one in
  // `onOriginPicked` had already drifted by omitting `originChosen`.
  const disabled = volume === "" || flawStatus === "" || !originChosen

  useEffect(() => inputRef.current.focus(), [])

  const onChange = (e) => {
    setValue(e.target.value)
  }

  const onVolumeChange = (e) => {
    // The prototype both warns and brings the quantity back to the stock, so the message stays
    // reachable instead of being made impossible by the clamp.
    const { tooMuch, kept } = capTo(availableStock, e.target.value)

    setOverflow(tooMuch)
    lostActionInfos[2](kept)
    setVolume(kept)
  }

  const onReasonChange = (status) => {
    lostActionInfos[1](status)
    setFlawStatus(status)
  }

  const onOriginPicked = (type) => {
    // The quantity field holds the focus on open, so it is usually filled before the fridge is
    // picked — and a quantity typed then was capped against nothing. Capping it here is what
    // makes the limit hold whatever the order of entry, including a switch to a smaller fridge.
    const { tooMuch, kept } = capTo(stockForType(productStock, type), volume)

    setOrigin(type)
    setOverflow(tooMuch)

    if (kept !== volume) {
      setVolume(kept)
      lostActionInfos[2](kept)
    }

    if (onOriginChange) onOriginChange(type)
  }

  return (
    <form onSubmit={handleValidate}>
      {/* text summary */}
      <div className={clsx(classes.withSelection, { "is-tablet": isTablet })}>
        Combien de <span>{flawProduct}</span> cassés ?
      </div>

      {hasTwoStocks && (
        <SelectFlawOrigin
          productStock={productStock}
          selected={origin}
          onChange={onOriginPicked}
        />
      )}

      {/* select flaw origin*/}
      <SelectFlaw
        className={classes.reasons}
        preselected={flawStatus}
        isTablet={isTablet}
        onReasonChange={onReasonChange}
      />

      {/* text summary */}
      <TextField
        inputProps={{
          className: clsx(classes.input1, { "is-tablet": isTablet }),
          placeholder: "000",
          inputMode: "numeric",
          pattern: "[0-9]*"
        }}
        variant="standard"
        value={volume}
        onChange={onVolumeChange}
        inputRef={inputRef}
      />

      {/* text summary */}
      <TextField
        variant="standard"
        inputProps={{
          className: classes.input,
          placeholder: "commentaire (facultatif)",
          type: "text",
        }}
        onChange={onChange}
      />

      {overflow && (
        <div className={classes.error}>
          Impossible de déclarer une quantité cassée supérieur à la quantité en stock
        </div>
      )}

      {/* button submit */}
      <ButtonFlawAction isTablet={isTablet} disabled={disabled} isFlaw={true}>
        Je confirme {lostActionInfos ? lostActionInfos[0] : ""} casses
      </ButtonFlawAction>
    </form>
  )
}

export default InputFlawNumberAndReason
